/**
 * Dictation for the composer: the browser's own speech recognition writes
 * into the input field while the user speaks (continuous, with interim
 * results), so the text can be followed live and edited before it is sent.
 * Nothing leaves the browser until the user sends — this only fills the
 * text field, same as typing. Hidden in `AgentChatBox` when unsupported
 * (`supported === false`).
 */
import React from "react";

type SpeechRecognitionResultLike = ArrayLike<{ transcript: string }> & {
  isFinal?: boolean;
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: (event: {
    results: ArrayLike<SpeechRecognitionResultLike>;
  }) => void;
  onend: () => void;
  onerror: () => void;
  start(): void;
  stop(): void;
  abort?(): void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function speechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Used when no UI language is known — the platform's default language. */
const FALLBACK_DICTATION_LANGUAGE = "de-DE";

/**
 * `start(base)`: the text already in the field; the dictation is appended
 * to it. `onText` receives the whole field value on every result.
 * `getLanguage` returns the UI language shown right now (the current
 * Tolgee language, e.g. "de" or "en") — read on every `start`, so the
 * recogniser follows a language switch.
 */
export function useDictation(
  onText: (text: string) => void,
  getLanguage: () => string | undefined = () => undefined
) {
  const [listening, setListening] = React.useState(false);
  const recognition = React.useRef<SpeechRecognitionLike | null>(null);
  const latestOnText = React.useRef(onText);
  latestOnText.current = onText;
  const latestGetLanguage = React.useRef(getLanguage);
  latestGetLanguage.current = getLanguage;
  const supported = React.useMemo(() => speechRecognitionCtor() !== null, []);

  // Set once the question is sent (`cancel()`): results that still arrive
  // (the browser keeps listening briefly, and the answer read aloud is
  // picked up by the microphone) must not land in the emptied field again.
  const ignoring = React.useRef(false);

  const stop = React.useCallback(() => recognition.current?.stop(), []);

  /** Ends the dictation and drops everything still in flight — the question was sent. */
  const cancel = React.useCallback(() => {
    ignoring.current = true;
    const r = recognition.current;
    if (!r) return;
    if (r.abort) r.abort();
    else r.stop();
  }, []);

  const start = React.useCallback((base: string) => {
    const Ctor = speechRecognitionCtor();
    if (!Ctor) return;
    const r = new Ctor();
    r.lang = latestGetLanguage.current() || FALLBACK_DICTATION_LANGUAGE;
    r.continuous = true;
    r.interimResults = true;
    const prefix = base.trim() ? `${base.trimEnd()} ` : "";
    ignoring.current = false;
    r.onresult = event => {
      if (ignoring.current) return;
      const spoken = Array.from(event.results)
        .map(result => result[0]?.transcript ?? "")
        .join("");
      latestOnText.current(prefix + spoken);
    };
    r.onend = () => {
      recognition.current = null;
      setListening(false);
    };
    r.onerror = () => r.stop();
    recognition.current = r;
    setListening(true);
    r.start();
  }, []);

  React.useEffect(() => () => recognition.current?.stop(), []);

  return { supported, listening, start, stop, cancel };
}
